/**
 * Planon (CAFM) person-push shared types.
 *
 * The reference-data "kinds" are the cached Planon lookups that back the
 * create-person dropdowns. Each cached row is a { syscode, label } option;
 * some kinds carry more (the roster's email/code/position, a picklist's
 * group_code, a property's Code) in the row payload on the API side.
 */

export type PlanonReferenceKind =
  | 'roster'
  | 'picklist'
  | 'cost_centre'
  | 'country'
  | 'department'
  | 'employment_type'
  | 'trade'
  | 'tariff_group'
  | 'position'
  | 'property';

/** One dropdown option as returned to the admin UI. */
export interface PlanonReferenceEntry {
  kind: PlanonReferenceKind;
  syscode: number;
  label: string;
  /** Set only for the 'picklist' kind (the field-group link), else null. */
  group_code: string | null;
  /** Kind extras: roster carries email/code/position, property its Code. */
  payload: Record<string, unknown>;
}

/**
 * Fixed option sets and constants for the person create form. These are not
 * queryable in Planon (no *WelcomeQuery), so they live here as the single
 * source of truth for the create-person dropdowns and the payload builder.
 */

/** GenderRef syscodes (sent as a number in the BomAdd values). */
export const PLANON_GENDER_OPTIONS = [
  { syscode: 2500, label: 'Male' },
  { syscode: 2501, label: 'Female' },
  { syscode: 3491, label: 'X' },
] as const;

/**
 * PersonTypeRef selection. Multi-select; sent as a positional space string
 * (position k = digit k when type k is selected, else a space), e.g. type 2
 * alone = " 2", types 1 and 3 = "1 3".
 */
export const PLANON_PERSON_TYPE_OPTIONS = [
  { code: 1, label: 'Internal coordinator' },
  { code: 2, label: 'Internal tradesperson' },
  { code: 3, label: 'Requestor' },
  { code: 4, label: 'Customer' },
  { code: 5, label: 'Supervisor' },
  { code: 6, label: 'External contact' },
  { code: 7, label: 'Budget responsible/Manager' },
  { code: 8, label: 'Administrator' },
  { code: 9, label: 'External Client Escalation' },
  { code: 10, label: 'Other' },
] as const;

/** HCC zones (FreeString9 / FreeString21). Free text in Planon; dropdown here. */
export const PLANON_ZONE_OPTIONS = [
  'Zone 1',
  'Zone 2',
  'Zone 3',
  'Zone 4',
  'Zone 5',
  'Zone 6',
] as const;

/** United Kingdom, the Country2 default when none is chosen. */
export const PLANON_DEFAULT_COUNTRY_SYSCODE = 1;
/** MultiCalendarRef is fixed for now (availability calendar). */
export const PLANON_MULTI_CALENDAR_SYSCODE = 2;
